"use client";

import { useState } from "react";
import { createPortal } from "react-dom";

import type { ClaimPreviewResponse } from "@/lib/api/claims";
import { OctopusGlyph } from "@/components/OctopusGlyph";

/**
 * Broadcast email preview (broadcast Day 5).
 *
 * Promoted from the approved compare mockup
 * (web/app/mockup/compare/page.tsx EmailPreviewModal), now wired to the
 * REAL composed email. The single-shop popup is NegotiationModal; this is
 * its group sibling. The one real difference the owner asked for: it shows
 * ONE representative email that each ticked shop receives on its own, plus
 * the shop picker ("exactly who") that the page's one button deferred here.
 *
 * The email itself is the live preview from POST /api/claims/preview for
 * the cheapest ticked shop (the representative): From / To / Subject / body
 * verbatim, INCLUDING the mandatory disclosure footer the composer already
 * appended (so no separate footer block - what you see is what sends). The
 * body is fresh-written per shop at send time, so this is a true sample,
 * not the exact bytes every shop gets.
 */

const ACCENT = "#f6c453";
const FG = "hsl(var(--foreground))";
const FG2 = "hsl(var(--muted-foreground))";
const FG3 = "hsl(var(--muted-foreground))"; // 2026-07-29 a11y: was #8b8b94 (5.78:1); 10.81:1 on the dark modal, matches the app-wide muted tier
const LINE = "hsl(var(--border))";
const LINE_STRONG = "hsl(var(--border))";

const SENDER_ADDRESS = "claims@octopusden.app";

/** Strip a leading greeting paragraph ("Hello X," / "Hi X,") from the
 * PREVIEW body only. A broadcast asks several shops and each receives its
 * OWN copy, greeted by its own name, so showing one shop's greeting in the
 * single shared preview is misleading. The greeting is removed from the
 * DISPLAY only; every sent email keeps its per-shop greeting. Fail-safe: if
 * the first paragraph is not a greeting, the input is returned unchanged. */
const _GREETING_RX =
  /^\s*(hello|hi|dear|greetings|good (?:morning|afternoon|evening|day))\b/i;

function _escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Turn a shop-specific greeting ("Hello there The Good Guys,") into a
 * shop-NEUTRAL one ("Hello there,") for the shared broadcast preview: keep
 * the fresh greeting word, drop the shop name and a trailing "team". The
 * one preview stands in for N shops, so it must not be addressed to a single
 * one; every SENT email keeps its own per-shop greeting. Falls back to a
 * plain "Hello," if the line reduces to nothing. */
function neutraliseGreetingLine(line: string, shopName: string): string {
  let g = line;
  if (shopName) g = g.replace(new RegExp(_escapeRe(shopName), "ig"), " ");
  g = g
    .replace(/\bteam\b/gi, " ")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.!?])/g, "$1")
    .replace(/[ ,]+$/g, "")
    .trim();
  if (!g) return "Hello,";
  if (!/[,.!?]$/.test(g)) g += ",";
  return g;
}

function neutraliseGreetingHtml(html: string, shopName: string): string {
  return html.replace(/^(\s*<p>)([^<]*)(<\/p>)/i, (m, open, text, close) => {
    if (!_GREETING_RX.test(text)) return m; // first para is not a greeting
    return `${open}${neutraliseGreetingLine(text, shopName)}${close}`;
  });
}

function neutraliseGreetingText(text: string, shopName: string): string {
  const paras = text.split(/\n\n+/);
  if (paras.length > 0 && _GREETING_RX.test(paras[0])) {
    paras[0] = neutraliseGreetingLine(paras[0], shopName);
  }
  return paras.join("\n\n");
}

export interface AskableShop {
  /** retailer_slug - the stable key used for tick/untick + the payload. */
  slug: string;
  /** Prettified display name ("JB Hi-Fi"). */
  name: string;
}

export function EmailPreviewModal({
  representativeName,
  preview,
  askableShops,
  excluded,
  toggleShop,
  tickedCount,
  maxAsk,
  userName,
  sending,
  error,
  onSend,
  onClose,
}: {
  /** Prettified name of the shop whose email is previewed (cheapest ticked). */
  representativeName: string;
  /** The live composed email; null while it is still being written. */
  preview: ClaimPreviewResponse | null;
  askableShops: AskableShop[];
  excluded: Set<string>;
  toggleShop: (slug: string) => void;
  tickedCount: number;
  maxAsk: number;
  userName: string;
  sending: boolean;
  error: string | null;
  onSend: () => void;
  onClose: () => void;
}) {
  const [consent, setConsent] = useState(true);

  // The email is still being written (~2s): the modal is already open and
  // the email area shows a shimmer until the preview lands.
  const writing = preview === null;
  // A broadcast asks several shops, each greeted by its own name. So the
  // shared preview shows the message shop-NEUTRAL: the "To" line names the
  // group, not one shop, and the shop-specific greeting is dropped from the
  // display (the sent copies keep their own greeting). A single-shop ask
  // still shows that one shop by name.
  const multiShop = tickedCount > 1;
  const toDisplay = multiShop
    ? "Each shop you selected"
    : preview?.to_display ?? `The ${representativeName} team`;
  const subject = preview?.composed_subject ?? "";
  const rawBody = preview?.composed_body ?? "";
  const body = multiShop
    ? neutraliseGreetingText(rawBody, representativeName)
    : rawBody;
  // Signed-off template ships a clickable HTML body (all data fields escaped
  // server-side, so it is safe to render), same as NegotiationModal. Prefer
  // it; fall back to plain text.
  const rawBodyHtml = preview?.composed_body_html ?? "";
  const bodyHtml = multiShop
    ? neutraliseGreetingHtml(rawBodyHtml, representativeName)
    : rawBodyHtml;
  const atCap = tickedCount >= maxAsk;
  const canSend = !writing && !sending && consent && tickedCount > 0;

  const shimmer = (w: string) => (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        height: 11,
        width: w,
        borderRadius: 4,
        background:
          "linear-gradient(90deg, hsl(var(--foreground) / 0.04), hsl(var(--foreground) / 0.10), hsl(var(--foreground) / 0.04))",
      }}
    />
  );

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Best price request"
      onClick={(e) => {
        if (e.target === e.currentTarget && !sending) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(5,5,6,0.72)",
        backdropFilter: "blur(7px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        zIndex: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 700,
          maxHeight: "min(92vh, 860px)",
          overflowY: "auto",
          background: "hsl(var(--secondary))",
          border: `1px solid ${LINE}`,
          borderRadius: 22,
          boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
        }}
      >
        {/* head: brand row + title */}
        <div style={{ padding: "22px 30px 20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            {/* Brand lockup — matches the site header: octopus glyph on the
                amber tile + "Octopusden" in title case (not a bare "O" / caps). */}
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 8,
                  background: "linear-gradient(135deg, #fde68a 0%, #fbbf24 100%)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <OctopusGlyph size={17} />
              </span>
              <span
                style={{
                  fontSize: "var(--fs-small)",
                  letterSpacing: "-0.01em",
                  color: FG,
                  fontWeight: 600,
                }}
              >
                Octopusden
              </span>
            </div>
            <button
              type="button"
              onClick={() => !sending && onClose()}
              aria-label="Close"
              style={{
                width: 31,
                height: 31,
                borderRadius: 9,
                border: `1px solid ${LINE_STRONG}`,
                background: "hsl(var(--secondary))",
                color: FG3,
                fontSize: "var(--fs-body)",
                cursor: "pointer",
              }}
            >
              &times;
            </button>
          </div>

          <h2
            style={{
              margin: "16px 0 0",
              fontSize: "1.25rem",
              fontWeight: 600,
              letterSpacing: "-0.015em",
              lineHeight: 1.3,
              color: FG,
            }}
          >
            Ask {tickedCount} {tickedCount === 1 ? "shop" : "shops"} for their
            best price
          </h2>
          <p style={{ margin: "8px 0 0", fontSize: "var(--fs-small)", color: FG3 }}>
            {multiShop
              ? "This is the message each shop receives, addressed to them by name."
              : "Here is the message we'll send."}
          </p>
        </div>

        {/* the email itself (live preview) */}
        <div style={{ padding: "0 30px 20px" }}>
          <div
            style={{
              border: `1px solid ${LINE}`,
              borderRadius: 14,
              background: "hsl(var(--background))",
            }}
          >
            <div style={{ padding: "16px 22px", display: "grid", gap: 9 }}>
              {(["From", "To", "Subject"] as const).map((lab) => (
                <div
                  key={lab}
                  style={{ display: "flex", gap: 10, fontSize: "var(--fs-small)" }}
                >
                  <span
                    className="uppercase"
                    style={{
                      width: 66,
                      flex: "none",
                      fontSize: "var(--fs-micro)",
                      letterSpacing: "0.1em",
                      color: FG3,
                      fontWeight: 600,
                      paddingTop: 2,
                    }}
                  >
                    {lab}
                  </span>
                  <span style={{ color: FG2, minWidth: 0 }}>
                    {lab === "From" ? (
                      <>
                        {userName} (via Octopusden) &lt;
                        <span style={{ color: ACCENT }}>{SENDER_ADDRESS}</span>
                        &gt;
                      </>
                    ) : lab === "To" ? (
                      <>{toDisplay}</>
                    ) : writing ? (
                      shimmer("62%")
                    ) : (
                      subject
                    )}
                  </span>
                </div>
              ))}
            </div>

            <div
              style={{
                borderTop: `1px solid ${LINE}`,
                padding: 22,
                fontSize: "var(--fs-base)",
                lineHeight: 1.78,
                color: FG2,
                whiteSpace: "pre-wrap",
              }}
            >
              {writing ? (
                <div style={{ display: "grid", gap: 11 }}>
                  {shimmer("95%")}
                  {shimmer("88%")}
                  {shimmer("55%")}
                </div>
              ) : bodyHtml ? (
                // Safe: generated server-side from our own template with all
                // data fields escaped (same as NegotiationModal).
                <div
                  className="mio-email-preview-body"
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />
              ) : (
                body
              )}
            </div>
          </div>

          {/* Each shop gets its OWN email. The picker lives here, not on the
              page: the page asks "do you want this", this handles "exactly
              who". Untick to drop a shop before it goes out. */}
          <div
            style={{
              marginTop: 14,
              borderRadius: 12,
              border: `1px solid ${LINE}`,
              background: "hsl(var(--secondary))",
              padding: "13px 16px",
            }}
          >
            <div
              style={{
                fontSize: "var(--fs-caption)",
                fontWeight: 600,
                color: FG2,
                marginBottom: 9,
              }}
            >
              {tickedCount} separate{" "}
              {tickedCount === 1 ? "email" : "emails"}, one per shop
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {askableShops.map((sh) => {
                const on = !excluded.has(sh.slug);
                const disabled = !on && atCap;
                return (
                  <button
                    key={sh.slug}
                    type="button"
                    onClick={() => !disabled && toggleShop(sh.slug)}
                    disabled={disabled}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      borderRadius: 999,
                      // Selected chip: theme-aware amber (deep readable amber
                      // on light, the original bright amber on dark) so it's
                      // visible on the light modal. Unselected: neutral outline.
                      border: `1px solid ${on ? "var(--amber-chip-border)" : LINE_STRONG}`,
                      background: on ? "var(--amber-chip-bg)" : "transparent",
                      color: on ? "var(--amber-chip-text)" : FG3,
                      fontSize: "var(--fs-micro)",
                      padding: "5px 11px",
                      whiteSpace: "nowrap",
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.45 : 1,
                    }}
                  >
                    <span aria-hidden="true" style={{ fontSize: "var(--fs-micro)" }}>
                      {on ? "✓" : "+"}
                    </span>
                    {sh.name}
                  </button>
                );
              })}
            </div>
            <p
              style={{
                margin: "11px 0 0",
                fontSize: "var(--fs-caption)",
                lineHeight: 1.6,
                color: FG3,
              }}
            >
              These are the shops most likely to beat this price. Add or remove
              any before you send.
            </p>
          </div>
        </div>

        {error !== null ? (
          <p
            style={{
              margin: "0 30px",
              fontSize: "var(--fs-caption)",
              color: "hsl(var(--destructive))",
              lineHeight: 1.5,
            }}
          >
            {error}
          </p>
        ) : null}

        {/* foot: consent + actions */}
        <div
          style={{
            borderTop: `1px solid ${LINE}`,
            padding: "14px 30px 18px",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 18,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              flex: "1 1 260px",
            }}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              style={{ accentColor: ACCENT, width: 16, height: 16 }}
            />
            <span style={{ fontSize: "var(--fs-caption)", color: FG3, lineHeight: 1.4 }}>
              Send as <b style={{ color: FG }}>{userName}</b>, sharing your name
              and email.
            </span>
          </label>

          <div style={{ display: "flex", gap: 10, flex: "none" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              style={{
                border: `1px solid ${LINE_STRONG}`,
                background: "transparent",
                color: FG2,
                fontSize: "var(--fs-small)",
                fontWeight: 500,
                borderRadius: 11,
                padding: "11px 18px",
                cursor: sending ? "default" : "pointer",
                opacity: sending ? 0.55 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              style={{
                border: 0,
                borderRadius: 11,
                padding: "11px 22px",
                fontWeight: 600,
                fontSize: "var(--fs-small)",
                color: "#1a1408",
                background: "linear-gradient(180deg, #fbd06b, #f6c453)",
                opacity: canSend ? 1 : 0.55,
                cursor: canSend ? "pointer" : "default",
                boxShadow: "0 6px 18px rgba(246,196,83,0.2)",
              }}
            >
              {sending ? "Sending…" : "Send on my behalf →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Portal to <body> so the fixed modal is never trapped inside a
  // transformed / backdrop-blurred comparison ancestor (the same fix
  // NegotiationModal needed).
  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
}
